import { degreeToRadian, getDirectionVectors } from '@/utils/map';
import { isValidNumber } from '@/utils/number';
import type { DabeeoMap } from 'dabeeomaps';
import { useCallback, useMemo } from 'react';

const STEP_SIZE = 10;

export const useMapMove = (currentMap?: DabeeoMap) => {
    const unitVectors = useMemo(() => {
        const rotation = currentMap?.control?.getCurrentCameraInfo?.()?.rotation ?? 0;
        const degree = degreeToRadian(rotation);
        return getDirectionVectors(degree);
    }, [currentMap]);

    /**
     * Generic move helper: shifts the map center by (dx, dy).
     */
    const moveBy = useCallback(
        (dx: number, dy: number) => {
            if (!currentMap) return;

            const center = currentMap?.control?.getCurrentCameraInfo?.()?.center;
            if (!isValidNumber(center?.x) || !isValidNumber(center?.y)) {
                return;
            }

            const newPosition = {
                x: center.x + dx,
                y: center.z + dy,
            };

            currentMap.control?.moveTo?.({ position: newPosition, transition: true });
        },
        [currentMap]
    );

    const moveUp = useCallback(() => moveBy(STEP_SIZE * unitVectors.up.x, STEP_SIZE * unitVectors.up.y), [moveBy, unitVectors]);
    const moveDown = useCallback(() => moveBy(STEP_SIZE * unitVectors.down.x, STEP_SIZE * unitVectors.down.y), [moveBy, unitVectors]);
    const moveLeft = useCallback(() => moveBy(STEP_SIZE * unitVectors.left.x, STEP_SIZE * unitVectors.left.y), [moveBy, unitVectors]);
    const moveRight = useCallback(() => moveBy(STEP_SIZE * unitVectors.right.x, STEP_SIZE * unitVectors.right.y), [moveBy, unitVectors]);

    return {
        moveUp,
        moveDown,
        moveLeft,
        moveRight,
    };
};
